"use client";

import { useEffect, useRef, useSyncExternalStore } from "react";
import {
    motion,
    useInView,
    useMotionValueEvent,
    useReducedMotion,
    useScroll,
    useTransform,
    MotionValue,
} from "framer-motion";
import { getAbAssignment, getAbPreview, sendAbEvent } from "@/lib/ab-client";

// The cookie-backed assignment never changes while the page is open.
const subscribe = () => () => {};
const noAssignment = () => null;

// While the homepage A/B test runs (`abRunning`), each visitor sees variant A
// or B. The server always renders A; the visitor's variant is picked from a
// cookie after hydration, before they've scrolled down to this section. With
// the test off, a drafted B can still be previewed with `?ab=b`.
export default function NarrativeIntro({
    text,
    variantB,
    abRunning = false,
}: {
    text: string;
    variantB?: string;
    abRunning?: boolean;
}) {
    // null on the server and during hydration, so the markup matches.
    const assignment = useSyncExternalStore(
        subscribe,
        !variantB ? noAssignment : abRunning ? getAbAssignment : getAbPreview,
        noAssignment
    );

    const shown = assignment?.variant === "b" && variantB ? variantB : text;
    const words = shown.split(/\s+/).filter(Boolean);
    const ref = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start 0.85", "end 0.6"],
    });

    // Funnel events: "exposure" once the section is on screen, "read_through"
    // once the scroll reveal reaches the last word.
    const tracking = assignment !== null && !assignment.preview;
    const inView = useInView(ref, { once: true, amount: 0.3 });
    const sent = useRef(new Set<string>());
    const send = (event: "exposure" | "read_through") => {
        if (sent.current.has(event)) return;
        sent.current.add(event);
        sendAbEvent(event);
    };
    useEffect(() => {
        if (tracking && inView) send("exposure");
    }, [tracking, inView]);
    useMotionValueEvent(scrollYProgress, "change", (p) => {
        if (tracking && p >= 0.98) {
            send("exposure");
            send("read_through");
        }
    });

    return (
        <section id="story" ref={ref} data-anchor className="relative h-[180vh]">
            <div className="sticky top-[var(--header-height)] flex min-h-[calc(100svh-var(--header-height))] items-center px-4 sm:px-16">
                <div className="mx-auto w-full max-w-4xl">
                    <h2 className="mb-7 font-mono text-xs uppercase tracking-[0.3em] text-primary">
                        01 — How I work
                    </h2>
                    {/* Screen readers get the sentence once, as plain text; the
                        per-word scroll animation is decorative. */}
                    <p className="sr-only">{shown}</p>
                    <p
                        aria-hidden="true"
                        className="flex flex-wrap font-heading text-[1.7rem] leading-snug sm:text-5xl sm:leading-[1.22]"
                    >
                        {words.map((word, i) => {
                            const start = i / words.length;
                            const end = start + 1 / words.length;
                            return (
                                <Word key={i} progress={scrollYProgress} range={[start, end]}>
                                    {word}
                                </Word>
                            );
                        })}
                    </p>
                </div>
            </div>
        </section>
    );
}

function Word({
    children,
    progress,
    range,
}: {
    children: string;
    progress: MotionValue<number>;
    range: [number, number];
}) {
    // With reduced motion, skip the scroll-driven reveal and show the words at
    // full contrast from the start.
    const reduce = useReducedMotion();
    const opacity = useTransform(progress, range, reduce ? [1, 1] : [0.15, 1]);
    return (
        <span className="relative mr-[0.35ch] mt-[0.12em]">
            <span className="opacity-15">{children}</span>
            <motion.span style={{ opacity }} className="absolute left-0 top-0">
                {children}
            </motion.span>
        </span>
    );
}
